const TEST_CPFS = new Set(['00000000000', '11111111111']);

export function normalizeCpfCnpj(value: string | null | undefined): string {
  if (!value) return '';
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function isValidCpf(value: string): boolean {
  if (!value) return false;
  const cpf = value.replace(/\D/g, '');
  if (TEST_CPFS.has(cpf)) return true;
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  const calcDigit = (base: string, factorStart: number): number => {
    const sum = base
      .split('')
      .reduce((acc, digit, idx) => acc + parseInt(digit, 10) * (factorStart - idx), 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };

  const base9 = cpf.slice(0, 9);
  const d1 = calcDigit(base9, 10);
  const base10 = base9 + d1.toString();
  const d2 = calcDigit(base10, 11);

  return cpf.endsWith(`${d1}${d2}`);
}

function cnpjCharValue(char: string): number {
  return char.charCodeAt(0) - 48;
}

function isValidCnpj(value: string): boolean {
  if (!value) return false;
  const cnpj = normalizeCpfCnpj(value);
  if (cnpj.length !== 14) return false;
  if (/^(.)\1{13}$/.test(cnpj)) return false;

  const calcCheckDigit = (base: string, factors: number[]): number => {
    const sum = base
      .split('')
      .reduce((acc, char, idx) => acc + cnpjCharValue(char) * factors[idx], 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };

  const base12 = cnpj.slice(0, 12);
  const d1 = calcCheckDigit(base12, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const base13 = base12 + d1.toString();
  const d2 = calcCheckDigit(base13, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

  return cnpj.endsWith(`${d1}${d2}`);
}

export function isValidCpfCnpj(value: string): boolean {
  if (!value) return false;
  const cleaned = normalizeCpfCnpj(value);
  if (cleaned.length === 11) return isValidCpf(cleaned);
  if (cleaned.length === 14) return isValidCnpj(cleaned);
  return false;
}

export function isCnpj(value: string | null | undefined): boolean {
  return /^[A-Z0-9]{12}\d{2}$/.test(normalizeCpfCnpj(value));
}

export function isCpf(value: string | null | undefined): boolean {
  return /^\d{11}$/.test(normalizeCpfCnpj(value));
}

export function isCpfCnpj(value: string | null | undefined): boolean {
  return isCpf(value) || isCnpj(value);
}
